import { BaseServiceSetup, communicationService, eventsRepository, utils } from '@wikia/ad-engine';
import { Injectable } from '@wikia/dependency-injection';

const logGroup = 'UcpDesktopIcbOutOfPopularPagesSetup';

@Injectable()
export class UcpDesktopIcbOutOfPopularPagesSetup extends BaseServiceSetup {
	call(): void {
		this.movePopularPagesOutofStickyModuleWrapper();
	}

	private readonly POPULAR_PAGES_SELECTOR = '#WikiaAdInContentPlaceHolder';
	private readonly STICKY_MODULE_SELECTOR = '.sticky-modules-wrapper';

	/**
	 * The popular pages module and the new wiki recommendations module don't load in at page load time.
	 * We need to ta wait for the new wiki recommendations module to load in, and move the popular pages module
	 * right after it. We can't move the popular pages module right before the sticky wrapper, because sometimes
	 * the new wiki recommendations module loads in after the popular pages module loads, causing the popular pages module
	 * to be added in the wrong place.
	 *
	 * The purpose of this function is to wait for the new wiki recommendations to be added as a child nodeto the
	 * right rail wrapper element. Once that occurs, we can proceed with moving the popular pages module out of the sticky wrapper.
	 * This logic to allow the Incontent Box Ad to be moved out of the Popular Pages module, and prevent the
	 * Popular Pages module from being sticky. Only the Incontent Box Ad should be sticky.
	 * */
	private movePopularPagesOutofStickyModuleWrapper(): void {
		const movePopularPages = (insertPosition: InsertPosition, element?: Element) => {
			const popularPages = document.querySelector(this.POPULAR_PAGES_SELECTOR) as HTMLElement;
			if (!element) {
				utils.logger(logGroup, 'Node not found. Cannot move popular pages.');
				return;
			}

			if (popularPages) {
				utils.logger(logGroup, `Moving popularPages ${insertPosition} of node:`, element);
				element?.insertAdjacentElement(insertPosition, popularPages);

				// Add classes to popular pages to make it sticky
				popularPages.classList.add(utils.STICKY_RAIL_MODULE_CONTAINER_CLASS);
				popularPages.firstElementChild?.classList.add(utils.STICKY_RAIL_MODULE_STICKY_CLASS);
			} else {
				utils.logger(logGroup, 'Popular pages not found. Cannot move popular pages.');
			}
		};

		// Reference:
		// https://github.com/Wikia/silver-surfer/blob/master/src/pathfinder/modules/OnWikiRecommendations/helpers/renderNewModule.ts#L25
		window.addEventListener('NewWikiRecommendationsLoaded', () => {
			utils.logger(logGroup, 'New Wiki Recommendations module was loaded.');
			const wikiRecommendationsModule = document.querySelector(
				'.render-new-wiki-recommendations.rail-module',
			) as Element;
			movePopularPages('afterend', wikiRecommendationsModule);
		});

		communicationService.on(eventsRepository.RAIL_READY, () => {
			utils.logger(logGroup, 'Rail is ready.');
			setTimeout(() => {
				const stickyModule = document.querySelector(this.STICKY_MODULE_SELECTOR) as Element;
				movePopularPages('beforebegin', stickyModule);
				if (utils.isOneOfExperimentsActiveFromContext(['stickyRightRailWithICB'])) {
					// TODO: For now, hide the sticky module and figure out a better solution.
					//  Maybe it should be removed from the DOM instead?
					(stickyModule as HTMLElement).style.display = 'none';
				}
			}, 1000);
		});
	}
}
